import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { CircleAlert, CircleCheck, CircleX, Info, X } from 'lucide-react';
import { useI18n } from '../../i18n';
import { STANDARD_MESSAGES } from './messages';
import ActionButton from './ActionButton';

const NotificationContext = createContext(null);

const TYPE_THEME = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  error: 'border-rose-200 bg-rose-50 text-rose-700',
  info: 'border-blue-200 bg-blue-50 text-blue-700',
};

const TYPE_ICON = {
  success: CircleCheck,
  warning: CircleAlert,
  error: CircleX,
  info: Info,
};

function NotificationProvider({ children }) {
  const { t } = useI18n();
  const [items, setItems] = useState([]);

  const remove = useCallback((id) => {
    setItems((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback((type, text) => {
    const id = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const createdAt = Date.now();
    setItems((prev) => {
      const duplicate = prev.some((item) => item.type === type && item.text === text && createdAt - (item.createdAt || 0) < 1500);
      if (duplicate) {
        return prev;
      }

      return [...prev, { id, type, text, createdAt }];
    });

    window.setTimeout(() => {
      remove(id);
    }, 4500);
  }, [remove]);

  const value = useMemo(() => ({
    notify,
    success: (text = t('common.savedSuccessfully', { defaultValue: STANDARD_MESSAGES.saveSuccess })) => notify('success', text),
    warning: (text = t('common.requiredWarning', { defaultValue: STANDARD_MESSAGES.requiredWarning })) => notify('warning', text),
    error: (text = t('common.saveError', { defaultValue: STANDARD_MESSAGES.saveError })) => notify('error', text),
    info: (text = t('common.importSuccess', { defaultValue: STANDARD_MESSAGES.csvImportInfo })) => notify('info', text),
  }), [notify, t]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <div className="pointer-events-none fixed inset-x-3 top-3 z-[1200] flex flex-col gap-2 sm:inset-x-auto sm:right-4 sm:top-4 sm:w-[min(92vw,400px)]">
        {items.map((item) => (
          <div key={item.id} className={`pointer-events-auto rounded-xl border px-4 py-3 text-sm font-semibold leading-6 shadow-lg shadow-slate-900/10 ${TYPE_THEME[item.type] || TYPE_THEME.info}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-2">
                {(() => {
                  const Icon = TYPE_ICON[item.type] || TYPE_ICON.info;
                  return <Icon size={18} strokeWidth={2} className="mt-1 shrink-0" aria-hidden="true" />;
                })()}
                <span className="min-w-0 break-words">{item.text}</span>
              </div>
              <ActionButton type="button" onClick={() => remove(item.id)} variant="secondary" className="min-h-8 shrink-0 px-2 py-1 text-xs" aria-label={t('common.closeNotification')}><X size={16} strokeWidth={2} aria-hidden="true" /></ActionButton>
            </div>
          </div>
        ))}
      </div>
    </NotificationContext.Provider>
  );
}

export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}

export default NotificationProvider;
